import React, { useState, useMemo, useCallback, useRef } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Download } from "lucide-react";
import { BenchmarkFooter } from "./benchmark-footer";
import { MockBenchmarkSection } from "./mock-benchmark";

/* -- Types ------------------------------------------------ */

interface BenchmarkRow {
  model: string;
  vendor: string;
  accuracy: number;
  meanElapsed: number;
  meanParseSuccess: number;
  costPerProblem: number;
}

type ProblemSet = "h200" | "g200";
type SortKey = "model" | "accuracy" | "meanElapsed" | "meanParseSuccess" | "costPerProblem";
type SortDir = "asc" | "desc";

/* -- Vendor colors ---------------------------------------- */

const VENDOR_COLORS: Record<string, string> = {
  Alibaba: "#F5A623",
  Anthropic: "#D0826C",
  DeepSeek: "#4A5FA8",
  Google: "#4285F4",
  MiniMax: "#E05A8D",
  "Moonshot AI": "#5BBFCF",
  OpenAI: "#3A9E6B",
  StepFun: "#9B79D9",
  Trinity: "#2E8B57",
  xAI: "#333333",
  "Zhipu AI": "#D44D4D",
};

/* -- Data ------------------------------------------------- */

const H200_DATA: BenchmarkRow[] = [
  { model: "claude-haiku-4-5", vendor: "Anthropic", accuracy: 59.0, meanElapsed: 198.4, meanParseSuccess: 95.0, costPerProblem: 0.005 },
  { model: "claude-opus-4-5", vendor: "Anthropic", accuracy: 63.0, meanElapsed: 220.1, meanParseSuccess: 100.0, costPerProblem: 0.026 },
  { model: "claude-opus-4-6", vendor: "Anthropic", accuracy: 63.0, meanElapsed: 218.6, meanParseSuccess: 100.0, costPerProblem: 0.038 },
  { model: "claude-sonnet-4-5", vendor: "Anthropic", accuracy: 47.0, meanElapsed: 205.8, meanParseSuccess: 100.0, costPerProblem: 0.015 },
  { model: "claude-sonnet-4-6", vendor: "Anthropic", accuracy: 63.0, meanElapsed: 213.7, meanParseSuccess: 94.0, costPerProblem: 0.022 },
  { model: "deepseek-v3-2", vendor: "DeepSeek", accuracy: 67.0, meanElapsed: 310.2, meanParseSuccess: 99.0, costPerProblem: 0.002 },
  { model: "gemini-2-0-flash", vendor: "Google", accuracy: 41.0, meanElapsed: 208.5, meanParseSuccess: 100.0, costPerProblem: 0.001 },
  { model: "gemini-2-5-flash", vendor: "Google", accuracy: 60.0, meanElapsed: 224.3, meanParseSuccess: 100.0, costPerProblem: 0.017 },
  { model: "gemini-2-5-flash-lite", vendor: "Google", accuracy: 44.0, meanElapsed: 218.9, meanParseSuccess: 100.0, costPerProblem: 0.003 },
  { model: "gemini-3-1-pro-preview", vendor: "Google", accuracy: 88.0, meanElapsed: 278.5, meanParseSuccess: 98.5, costPerProblem: 0.077 },
  { model: "gemini-3-flash-preview", vendor: "Google", accuracy: 53.5, meanElapsed: 205.7, meanParseSuccess: 100.0, costPerProblem: 0.005 },
  { model: "gemini-3-flash-preview-medium", vendor: "Google", accuracy: 72.0, meanElapsed: 225.4, meanParseSuccess: 100.0, costPerProblem: 0.017 },
  { model: "gemini-3-pro-preview", vendor: "Google", accuracy: 72.0, meanElapsed: 275.8, meanParseSuccess: 99.0, costPerProblem: 0.103 },
  { model: "glm-5", vendor: "Zhipu AI", accuracy: 50.0, meanElapsed: 465.3, meanParseSuccess: 93.0, costPerProblem: 0.033 },
  { model: "gpt-5-1-high", vendor: "OpenAI", accuracy: 68.0, meanElapsed: 560.8, meanParseSuccess: 95.0, costPerProblem: 0.116 },
  { model: "gpt-5-2", vendor: "OpenAI", accuracy: 85.0, meanElapsed: 355.4, meanParseSuccess: 99.5, costPerProblem: 0.116 },
  { model: "gpt-5-2-chat", vendor: "OpenAI", accuracy: 65.0, meanElapsed: 222.6, meanParseSuccess: 100.0, costPerProblem: 0.021 },
  { model: "gpt-5-nano", vendor: "OpenAI", accuracy: 47.0, meanElapsed: 298.3, meanParseSuccess: 100.0, costPerProblem: 0.005 },
  { model: "gpt-oss-120b", vendor: "OpenAI", accuracy: 47.0, meanElapsed: 224.5, meanParseSuccess: 97.5, costPerProblem: 0.001 },
  { model: "grok-4-1-fast", vendor: "xAI", accuracy: 72.0, meanElapsed: 270.2, meanParseSuccess: 100.0, costPerProblem: 0.004 },
  { model: "kimi-k2-5", vendor: "Moonshot AI", accuracy: 63.5, meanElapsed: 490.7, meanParseSuccess: 94.0, costPerProblem: 0.029 },
  { model: "minimax-m2-5", vendor: "MiniMax", accuracy: 44.0, meanElapsed: 365.1, meanParseSuccess: 91.0, costPerProblem: 0.011 },
  { model: "qwen3-5-397b-a17b", vendor: "Alibaba", accuracy: 64.0, meanElapsed: 415.2, meanParseSuccess: 98.0, costPerProblem: 0.037 },
  { model: "step-3-5-flash", vendor: "StepFun", accuracy: 63.0, meanElapsed: 380.5, meanParseSuccess: 84.0, costPerProblem: 0.007 },
  { model: "trinity-large-preview-free", vendor: "Trinity", accuracy: 47.0, meanElapsed: 435.8, meanParseSuccess: 100.0, costPerProblem: 0.0 },
];

const G200_DATA: BenchmarkRow[] = [
  { model: "claude-haiku-4-5", vendor: "Anthropic", accuracy: 46.5, meanElapsed: 206.8, meanParseSuccess: 94.5, costPerProblem: 0.005 },
  { model: "claude-opus-4-5", vendor: "Anthropic", accuracy: 66.0, meanElapsed: 213.5, meanParseSuccess: 100.0, costPerProblem: 0.026 },
  { model: "claude-opus-4-6", vendor: "Anthropic", accuracy: 66.5, meanElapsed: 224.5, meanParseSuccess: 100.0, costPerProblem: 0.038 },
  { model: "claude-sonnet-4-5", vendor: "Anthropic", accuracy: 46.5, meanElapsed: 212.2, meanParseSuccess: 100.0, costPerProblem: 0.015 },
  { model: "claude-sonnet-4-6", vendor: "Anthropic", accuracy: 76.5, meanElapsed: 218.4, meanParseSuccess: 92.5, costPerProblem: 0.022 },
  { model: "deepseek-v3-2", vendor: "DeepSeek", accuracy: 52.5, meanElapsed: 324.6, meanParseSuccess: 99.5, costPerProblem: 0.002 },
  { model: "gemini-2-0-flash", vendor: "Google", accuracy: 45.5, meanElapsed: 212.6, meanParseSuccess: 100.0, costPerProblem: 0.001 },
  { model: "gemini-2-5-flash", vendor: "Google", accuracy: 46.5, meanElapsed: 227.6, meanParseSuccess: 100.0, costPerProblem: 0.017 },
  { model: "gemini-2-5-flash-lite", vendor: "Google", accuracy: 45.5, meanElapsed: 221.3, meanParseSuccess: 100.0, costPerProblem: 0.003 },
  { model: "gemini-3-1-pro-preview", vendor: "Google", accuracy: 97.0, meanElapsed: 285.2, meanParseSuccess: 98.0, costPerProblem: 0.077 },
  { model: "gemini-3-flash-preview", vendor: "Google", accuracy: 55.0, meanElapsed: 210.3, meanParseSuccess: 100.0, costPerProblem: 0.005 },
  { model: "gemini-3-flash-preview-medium", vendor: "Google", accuracy: 94.0, meanElapsed: 229.9, meanParseSuccess: 100.0, costPerProblem: 0.017 },
  { model: "gemini-3-pro-preview", vendor: "Google", accuracy: 97.0, meanElapsed: 282.4, meanParseSuccess: 99.5, costPerProblem: 0.103 },
  { model: "glm-5", vendor: "Zhipu AI", accuracy: 90.0, meanElapsed: 477.9, meanParseSuccess: 94.0, costPerProblem: 0.033 },
  { model: "gpt-5-1-high", vendor: "OpenAI", accuracy: 87.5, meanElapsed: 572.2, meanParseSuccess: 94.5, costPerProblem: 0.116 },
  { model: "gpt-5-2", vendor: "OpenAI", accuracy: 95.5, meanElapsed: 369.0, meanParseSuccess: 99.0, costPerProblem: 0.116 },
  { model: "gpt-5-2-chat", vendor: "OpenAI", accuracy: 95.5, meanElapsed: 228.8, meanParseSuccess: 100.0, costPerProblem: 0.021 },
  { model: "gpt-5-nano", vendor: "OpenAI", accuracy: 75.5, meanElapsed: 304.6, meanParseSuccess: 100.0, costPerProblem: 0.005 },
  { model: "gpt-oss-120b", vendor: "OpenAI", accuracy: 90.5, meanElapsed: 228.8, meanParseSuccess: 97.0, costPerProblem: 0.001 },
  { model: "grok-4-1-fast", vendor: "xAI", accuracy: 94.5, meanElapsed: 278.7, meanParseSuccess: 100.0, costPerProblem: 0.004 },
  { model: "kimi-k2-5", vendor: "Moonshot AI", accuracy: 92.0, meanElapsed: 501.5, meanParseSuccess: 93.5, costPerProblem: 0.029 },
  { model: "minimax-m2-5", vendor: "MiniMax", accuracy: 81.5, meanElapsed: 370.4, meanParseSuccess: 90.5, costPerProblem: 0.011 },
  { model: "qwen3-5-397b-a17b", vendor: "Alibaba", accuracy: 97.0, meanElapsed: 425.7, meanParseSuccess: 98.5, costPerProblem: 0.037 },
  { model: "step-3-5-flash", vendor: "StepFun", accuracy: 83.0, meanElapsed: 389.8, meanParseSuccess: 83.5, costPerProblem: 0.007 },
  { model: "trinity-large-preview-free", vendor: "Trinity", accuracy: 46.0, meanElapsed: 441.0, meanParseSuccess: 100.0, costPerProblem: 0.0 },
];

/* -- Custom Legend ----------------------------------------- */

function ChartLegend() {
  const vendors = Object.entries(VENDOR_COLORS);
  return (
    <div className="flex flex-wrap justify-center gap-x-[20px] gap-y-[6px] mt-[16px]">
      {vendors.map(([v, c]) => (
        <div key={v} className="flex items-center gap-[6px]">
          <div className="size-[10px] rounded-full" style={{ backgroundColor: c }} />
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] opacity-70" style={{ color: 'var(--brand-text)' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

/* -- Scatter Chart Section -------------------------------- */

const CHART_W = 560;
const CHART_H = 380;
const PAD = { top: 16, right: 24, bottom: 48, left: 52 };

function toSvgX(cost: number) {
  const xMin = 0, xMax = 2;
  return PAD.left + ((cost - xMin) / (xMax - xMin)) * (CHART_W - PAD.left - PAD.right);
}
function toSvgY(acc: number) {
  const yMin = 35, yMax = 100;
  return PAD.top + ((yMax - acc) / (yMax - yMin)) * (CHART_H - PAD.top - PAD.bottom);
}

function SingleCostAccuracyChart({ data, title }: { data: BenchmarkRow[]; title: string }) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; row: BenchmarkRow } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const xTicks = [0, 0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
  const yTicks = [40, 50, 60, 70, 80, 90, 100];

  return (
    <div className="pb-[16px]" style={{ backgroundColor: 'var(--brand-bg)' }}>
      {/* Title */}
      <div className="pb-[16px] mb-[16px]" style={{ borderBottom: '1px solid var(--brand-border)' }}>
        <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[15px]" style={{ color: 'var(--brand-text)' }}>
          {title}
        </span>
      </div>

      {/* SVG Chart */}
      <div className="relative w-full" style={{ paddingBottom: `${(CHART_H / CHART_W) * 100}%` }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="absolute inset-0 w-full h-full"
          onMouseLeave={() => setTooltip(null)}
        >
          {yTicks.map(y => (
            <line key={`gy-${y}`} x1={PAD.left} y1={toSvgY(y)} x2={CHART_W - PAD.right} y2={toSvgY(y)}
              stroke="var(--brand-chart-grid)" strokeDasharray="3 3" />
          ))}
          {xTicks.map(x => (
            <line key={`gx-${x}`} x1={toSvgX(x)} y1={PAD.top} x2={toSvgX(x)} y2={CHART_H - PAD.bottom}
              stroke="var(--brand-chart-grid)" strokeDasharray="3 3" />
          ))}
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={CHART_H - PAD.bottom} stroke="var(--brand-chart-axis)" />
          <line x1={PAD.left} y1={CHART_H - PAD.bottom} x2={CHART_W - PAD.right} y2={CHART_H - PAD.bottom} stroke="var(--brand-chart-axis)" />
          {xTicks.map(x => (
            <text key={`xt-${x}`} x={toSvgX(x)} y={CHART_H - PAD.bottom + 16}
              textAnchor="middle" fontSize={10} fill="var(--brand-text)" opacity={0.5} fontFamily="Helvetica Neue, sans-serif">
              {x === 0 ? "free" : `$${x.toFixed(2)}`}
            </text>
          ))}
          {yTicks.map(y => (
            <text key={`yt-${y}`} x={PAD.left - 8} y={toSvgY(y) + 4}
              textAnchor="end" fontSize={10} fill="var(--brand-text)" opacity={0.5} fontFamily="Helvetica Neue, sans-serif">
              {y}%
            </text>
          ))}
          <text x={PAD.left + (CHART_W - PAD.left - PAD.right) / 2} y={CHART_H - 4}
            textAnchor="middle" fontSize={12} fill="var(--brand-text)" opacity={0.4} fontFamily="Helvetica Neue, sans-serif">
            Cost per problem (USD)
          </text>
          <text x={12} y={PAD.top + (CHART_H - PAD.top - PAD.bottom) / 2}
            textAnchor="middle" fontSize={12} fill="var(--brand-text)" opacity={0.4} fontFamily="Helvetica Neue, sans-serif"
            transform={`rotate(-90, 12, ${PAD.top + (CHART_H - PAD.top - PAD.bottom) / 2})`}>
            Accuracy (%)
          </text>
          {data.map((row, i) => {
            const cx = toSvgX(row.costPerProblem);
            const cy = toSvgY(row.accuracy);
            const fill = VENDOR_COLORS[row.vendor] || "#999";
            return (
              <circle key={`dot-${i}`} cx={cx} cy={cy} r={7} fill={fill} fillOpacity={0.85}
                className="cursor-pointer" onMouseEnter={() => setTooltip({ x: cx, y: cy, row })} />
            );
          })}
        </svg>

        {tooltip && (() => {
          const svgEl = svgRef.current;
          if (!svgEl) return null;
          const rect = svgEl.getBoundingClientRect();
          const scaleX = rect.width / CHART_W;
          const scaleY = rect.height / CHART_H;
          const px = tooltip.x * scaleX;
          const py = tooltip.y * scaleY;
          const flipX = px > rect.width * 0.65;
          const flipY = py > rect.height * 0.65;
          return (
            <div className="absolute z-10 shadow-lg px-[12px] py-[8px] pointer-events-none"
              style={{
                backgroundColor: 'var(--brand-surface)',
                border: '1px solid var(--brand-border-strong)',
                left: flipX ? undefined : px + 12,
                right: flipX ? rect.width - px + 12 : undefined,
                top: flipY ? undefined : py - 8,
                bottom: flipY ? rect.height - py - 8 : undefined,
              }}>
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0" style={{ color: 'var(--brand-text)', fontWeight: 600 }}>{tooltip.row.model}</p>
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] opacity-60 mt-[2px] mb-[6px]" style={{ color: 'var(--brand-text)' }}>{tooltip.row.vendor}</p>
              <div className="flex gap-[14px]">
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]" style={{ color: 'var(--brand-text)' }}>Accuracy: <strong>{tooltip.row.accuracy}%</strong></span>
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]" style={{ color: 'var(--brand-text)' }}>Cost: <strong>{tooltip.row.costPerProblem === 0 ? "free" : `$${tooltip.row.costPerProblem.toFixed(3)}`}</strong></span>
              </div>
            </div>
          );
        })()}
      </div>

      <ChartLegend />
      <p className="text-center mt-[12px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-40" style={{ color: 'var(--brand-text)' }}>
        Cost per problem vs. accuracy &middot; hover for model name
      </p>
    </div>
  );
}

/* -- Sortable Table Section ------------------------------- */

function SortButton({ label, active, dir, onClick }: { label: string; active: boolean; dir: SortDir; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="bg-transparent border-none cursor-pointer flex items-center gap-[4px] p-0"
    >
      <span className={`font-['Helvetica_Neue:Regular',sans-serif] text-[13px] ${active ? "" : "opacity-60"}`} style={{ color: 'var(--brand-text)' }}>
        {label}
      </span>
      {active ? (
        dir === "asc" ? <ArrowUp size={12} style={{ color: 'var(--brand-text)' }} /> : <ArrowDown size={12} style={{ color: 'var(--brand-text)' }} />
      ) : (
        <ArrowUpDown size={12} style={{ color: 'var(--brand-text)', opacity: 0.4 }} />
      )}
    </button>
  );
}

function DataTable({ data, problemSet, onProblemSetChange }: { data: BenchmarkRow[]; problemSet: ProblemSet; onProblemSetChange: (t: ProblemSet) => void }) {
  const [sortKey, setSortKey] = useState<SortKey>("model");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "model" ? "asc" : "desc");
    }
  }, [sortKey]);

  const sorted = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "model") cmp = a.model.localeCompare(b.model);
      else if (sortKey === "accuracy") cmp = a.accuracy - b.accuracy;
      else if (sortKey === "meanElapsed") cmp = a.meanElapsed - b.meanElapsed;
      else if (sortKey === "costPerProblem") cmp = a.costPerProblem - b.costPerProblem;
      else cmp = a.meanParseSuccess - b.meanParseSuccess;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [data, sortKey, sortDir]);

  const bestAccuracy = useMemo(() => Math.max(...data.map(d => d.accuracy)), [data]);
  const bestTime = useMemo(() => Math.min(...data.map(d => d.meanElapsed)), [data]);

  return (
    <div style={{ border: '1px solid var(--brand-border)', backgroundColor: 'var(--brand-surface)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-[12px] px-[16px] md:px-[24px] py-[12px] md:py-[16px]" style={{ borderBottom: '1px solid var(--brand-border)' }}>
        <div className="flex items-center gap-[12px]">
          <h3 className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] m-0" style={{ color: 'var(--brand-text)' }}>
            <div className="flex" style={{ border: '1px solid var(--brand-border-strong)' }}>
              {(["h200", "g200"] as ProblemSet[]).map((t) => (
                <button
                  key={t}
                  onClick={() => onProblemSetChange(t)}
                  className="border-none cursor-pointer px-[14px] py-[6px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] transition-colors"
                  style={{
                    backgroundColor: problemSet === t ? 'var(--brand-primary-btn-bg)' : 'transparent',
                    color: problemSet === t ? 'var(--brand-primary-btn-text)' : 'var(--brand-text-opacity-50)',
                  }}
                >
                  {t === "h200" ? "h200 (25)" : "g200 (25)"}
                </button>
              ))}
            </div>
          </h3>
        </div>
        <button className="flex items-center gap-[6px] bg-transparent cursor-pointer px-[12px] py-[6px] shrink-0" style={{ border: '1px solid var(--brand-border-strong)' }}>
          <Download size={14} style={{ color: 'var(--brand-text)' }} />
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]" style={{ color: 'var(--brand-text)' }}>Export CSV</span>
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]" style={{ borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--brand-border)' }}>
              <th className="text-left px-[16px] md:px-[24px] py-[12px]">
                <SortButton label="Model" active={sortKey === "model"} dir={sortDir} onClick={() => toggleSort("model")} />
              </th>
              <th className="text-right px-[16px] md:px-[24px] py-[12px]" style={{ width: '18%' }}>
                <div className="flex justify-end">
                  <SortButton label="Accuracy" active={sortKey === "accuracy"} dir={sortDir} onClick={() => toggleSort("accuracy")} />
                </div>
              </th>
              <th className="text-right px-[16px] md:px-[24px] py-[12px]" style={{ width: '18%' }}>
                <div className="flex justify-end">
                  <SortButton label="Cost" active={sortKey === "costPerProblem"} dir={sortDir} onClick={() => toggleSort("costPerProblem")} />
                </div>
              </th>
              <th className="text-right px-[16px] md:px-[24px] py-[12px]" style={{ width: '18%' }}>
                <div className="flex justify-end">
                  <SortButton label="Mean Elapsed (s)" active={sortKey === "meanElapsed"} dir={sortDir} onClick={() => toggleSort("meanElapsed")} />
                </div>
              </th>
              <th className="text-right px-[16px] md:px-[24px] py-[12px]" style={{ width: '18%' }}>
                <div className="flex justify-end">
                  <SortButton label="Mean Parse Success" active={sortKey === "meanParseSuccess"} dir={sortDir} onClick={() => toggleSort("meanParseSuccess")} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr
                key={row.model}
                style={{
                  borderBottom: '1px solid var(--brand-border-faint)',
                  backgroundColor: i % 2 === 1 ? 'var(--brand-surface-muted)' : undefined,
                }}
              >
                <td className="px-[16px] md:px-[24px] py-[10px]">
                  <div className="flex items-center gap-[8px]">
                    <div className="size-[8px] rounded-full shrink-0" style={{ backgroundColor: VENDOR_COLORS[row.vendor] || "#999" }} />
                    <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] whitespace-nowrap" style={{ color: 'var(--brand-text)' }}>{row.model}</span>
                  </div>
                </td>
                <td className="text-right px-[16px] md:px-[24px] py-[10px]"><span
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
                    style={{
                      color: 'var(--brand-text)',
                      fontWeight: row.accuracy >= 90 ? 600 : 400,
                      opacity: row.accuracy >= 90 ? 1 : 0.8,
                    }}
                  >{row.accuracy.toFixed(1)}%</span></td>
                <td className="text-right px-[16px] md:px-[24px] py-[10px]">
                  <span
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
                    style={{ color: 'var(--brand-text)', opacity: 0.8 }}
                  >
                    {row.costPerProblem === 0 ? "free" : `$${row.costPerProblem.toFixed(3)}`}
                  </span>
                </td>
                <td className="text-right px-[16px] md:px-[24px] py-[10px]">
                  <span
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
                    style={{
                      color: 'var(--brand-text)',
                      fontWeight: row.meanElapsed === bestTime ? 600 : 400,
                      opacity: 0.8,
                    }}
                  >
                    {row.meanElapsed.toFixed(1)}
                  </span>
                </td>
                <td className="text-right px-[16px] md:px-[24px] py-[10px]">
                  <span
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
                    style={{ color: 'var(--brand-text)', opacity: row.meanParseSuccess === 100 ? 0.8 : 0.6 }}
                  >
                    {row.meanParseSuccess.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -- Main Benchmark Page ---------------------------------- */

export default function Benchmark() {
  const [tableTab, setTableTab] = useState<ProblemSet>("h200");

  const tableData = tableTab === "h200" ? H200_DATA : G200_DATA;

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="px-[16px] md:px-[24px] pt-[24px] md:pt-[40px] pb-[0px] max-w-[1440px] mx-auto">

      {/* ── 1. Mock Benchmark Matrix ─────────────── */}
      <MockBenchmarkSection />

      {/* divider */}
      <div className="mt-[48px] mb-[40px]" style={{ borderTop: '1px solid var(--brand-border)' }} />

      {/* ── 2. Data Table (h200 / g200) ──────────── */}
      <DataTable data={tableData} problemSet={tableTab} onProblemSetChange={(t) => { setTableTab(t); }} />

      {/* ── 3. Two Charts Side by Side ───────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[32px] mt-[48px]">
        <SingleCostAccuracyChart data={H200_DATA} title="Hard" />
        <SingleCostAccuracyChart data={G200_DATA} title="Generated" />
      </div>

      {/* ── 4. Experiment Setup ──────────────────── */}
      <section
        id="experiment-setup"
        className="mt-[48px] md:mt-[80px] scroll-mt-[80px]"
        style={{
          backgroundColor: 'var(--brand-surface)',
          border: '1px solid var(--brand-border)',
        }}
      >
        <div className="px-[24px] md:px-[40px] lg:px-[56px] py-[32px] md:py-[48px]">
          <h2
            className="font-['Helvetica_Neue:Regular',sans-serif] m-0 text-[24px] md:text-[30px]"
            style={{ color: 'var(--brand-text)' }}
          >
            Experiment Setup
          </h2>
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] md:text-[15px] mt-[20px] mb-[16px] leading-[1.7] opacity-70"
            style={{ color: 'var(--brand-text)' }}
          >
            We prepare two problem sets, each containing 200 problems.
          </p>
          <ul className="pl-[20px] mb-[20px] space-y-[6px]" style={{ color: 'var(--brand-text)' }}>
            <li className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] md:text-[15px] leading-[1.7] opacity-70">
              <strong className="opacity-100" style={{ color: 'var(--brand-text)' }}>Generated</strong> — automatically generated: 109 true implications and 91 false implications.
            </li>
            <li className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] md:text-[15px] leading-[1.7] opacity-70">
              <strong className="opacity-100" style={{ color: 'var(--brand-text)' }}>Hard</strong> — manually curated by human experts: 74 true implications and 126 false implications.
            </li>
          </ul>
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] md:text-[15px] mt-[0] mb-[0] leading-[1.7] opacity-70"
            style={{ color: 'var(--brand-text)' }}
          >
            All experiments are run in parallel on an n2-standard-8 GCP virtual machine, with model access via OpenRouter. Models are selected based on their arena.ai rankings and openrouter usage at the end of February 2026. We use default model settings and do not enable reasoning modes unless a model uses reasoning by default or it is explicitly noted.
          </p>
        </div>
      </section>

      {/* ── 5. Optimized Prompt ──────────────────── */}
      <section
        id="optimized-prompt"
        className="mt-[48px] md:mt-[80px] scroll-mt-[80px]"
        style={{
          backgroundColor: 'var(--brand-surface)',
          border: '1px solid var(--brand-border)',
        }}
      >
        <div className="px-[24px] md:px-[40px] lg:px-[56px] py-[32px] md:py-[48px]">
          <h2
            className="font-['Helvetica_Neue:Regular',sans-serif] m-0 text-[24px] md:text-[30px]"
            style={{ color: 'var(--brand-text)' }}
          >
            Optimized Prompt
          </h2>
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] md:text-[15px] mt-[20px] mb-[28px] leading-[1.7] opacity-70"
            style={{ color: 'var(--brand-text)' }}
          >
            To remain model-agnostic while minimizing output formatting issues, we use a minimal prompt shared across all experiments. Rather than enforcing structured output formats such as JSON or XML — which were found to degrade performance in preliminary experiments — we adopt a section-header convention that is both human-readable and easy to parse reliably. Crucially, the prompt requires not only a binary verdict for automated evaluation, but also unstructured mathematical reasoning (proof or counterexample) for qualitative analysis.
          </p>

          {/* Code block */}
          <div
            className="p-[24px] md:p-[32px] overflow-x-auto"
            style={{
              backgroundColor: 'var(--brand-surface-muted)',
              border: '1px solid var(--brand-border)',
            }}
          >
            <pre
              className="font-['Courier_New',monospace] text-[13px] md:text-[14px] leading-[1.8] m-0 whitespace-pre-wrap"
              style={{ color: 'var(--brand-text)', opacity: 0.8 }}
            >
{`You are a mathematician specializing in equational theories of magmas.\nYour task is to determine whether `}<strong style={{ opacity: 1 }}>Equation 1</strong>{` ({{ equation1 }}) implies `}<strong style={{ opacity: 1 }}>Equation 2</strong>{` ({{ equation2 }}) over all magmas.\n<PLACEHOLDER_FOR_FUTURE_PROMPT_AUGMENTATIONS>\nOutput format (use exact headers without any additional text or formatting):\n`}<strong style={{ opacity: 1 }}>VERDICT:</strong>{` must be exactly TRUE or FALSE (in the same line).\n`}<strong style={{ opacity: 1 }}>REASONING:</strong>{` must be non-empty.\n`}<strong style={{ opacity: 1 }}>PROOF:</strong>{` required if `}<strong style={{ opacity: 1 }}>VERDICT is TRUE</strong>{`, empty otherwise.\n`}<strong style={{ opacity: 1 }}>COUNTEREXAMPLE:</strong>{` required if `}<strong style={{ opacity: 1 }}>VERDICT is FALSE</strong>{`, empty otherwise.`}
            </pre>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="mt-[60px] md:mt-[120px]">
        <BenchmarkFooter />
      </div>
    </div>
  );
}